import { type FC } from 'react';
import { type EditorConfiguration } from 'codemirror';
import { Controlled as CodeMirror } from 'react-codemirror2';
import { useCodeMirrorStrictModeHandlers } from '@editor2/hooks/useCodeMirrorStrictModeHandlers';
import { useFlushSyncCallback } from '@editor2/hooks/useFlushSyncCallback';

import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/javascript/javascript.js';

import 'codemirror/addon/lint/lint';
import 'codemirror/addon/lint/lint.css';
import 'codemirror/addon/lint/json-lint';

import 'codemirror/addon/fold/foldcode.js';
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/addon/fold/foldgutter.js';
import 'codemirror/addon/fold/brace-fold.js';

import 'codemirror/addon/display/autorefresh.js';

import jsonlint from 'jsonlint-mod';

import ShortcutsInfo from '@editor2/components/ShortcutsInfo';
import { jsonBlock } from './styles';

declare global {
	interface Window {
		jsonlint: any;
	}
}
window.jsonlint = jsonlint;

interface Props {
	value: string;
	options?: EditorConfiguration;
	showTips?: boolean;
	onChange: (value: string) => void;
}

const CodeMirrorJSON: FC<Props> = (props) => {
	const { value, options, showTips = true, onChange } = props;
	const { wrapperRef, handleEditorDidMount, handleEditorWillUnmount } =
		useCodeMirrorStrictModeHandlers();

	// Wrap onChange with flushSync for synchronous updates
	const syncOnChange = useFlushSyncCallback(onChange);

	const codeMirrorOptions: EditorConfiguration = {
		mode: 'application/json',
		lineNumbers: true,
		lineWrapping: true,
		autoRefresh: true,
		foldGutter: true,
		lint: true,
		gutters: ['CodeMirror-lint-markers', 'CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
		extraKeys: {
			Tab: false,
			'Shift-Tab': false,
			'Shift-Alt-F': 'indentAuto',
			'Shift-Option-F': 'indentAuto'
		},
		// Configure history for better undo/redo preservation
		undoDepth: 300,
		historyEventDelay: 2000
	};

	return (
		<div className={jsonBlock}>
			{showTips && <ShortcutsInfo />}
			<CodeMirror
				ref={wrapperRef}
				value={value}
				options={{ ...codeMirrorOptions, ...options }}
				onBeforeChange={(editor, data, value) => syncOnChange(value)}
				editorDidMount={handleEditorDidMount}
				editorWillUnmount={handleEditorWillUnmount}
			/>
		</div>
	);
};

export default CodeMirrorJSON;
